/**
 * SafetyBanner Component
 *
 * A dismissible banner at the top of the app that clearly communicates
 * this is NOT an official emergency service.
 *
 * This is a critical safety feature - users must understand they should
 * ALWAYS follow official emergency service instructions.
 *
 * Once dismissed, the banner stays hidden permanently (stored in localStorage).
 * It will only reappear if the user clears their browser data or reinstalls.
 */

const BANNER_DISMISSED_KEY = 'firewatch_safety_banner_dismissed';

/**
 * Check if the safety banner has been permanently dismissed.
 */
export function hasDismissedSafetyBanner(): boolean {
  try {
    return localStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Permanently dismiss the safety banner.
 */
export function dismissSafetyBanner(): void {
  try {
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  } catch {
    // Ignore localStorage errors
  }
}

import { AlertTriangleIcon } from './icons';

interface SafetyBannerProps {
  isVisible: boolean;
  onDismiss: () => void;
}

export default function SafetyBanner({
  isVisible,
  onDismiss,
}: SafetyBannerProps) {
  if (!isVisible) {
    return null;
  }

  return (
    <div className="safety-banner">
      <div className="safety-banner-content">
        <span className="safety-banner-icon" aria-hidden="true">
          <AlertTriangleIcon size={20} />
        </span>
        <div className="safety-banner-text">
          <p className="safety-banner-primary">
            <strong>NOT AN OFFICIAL EMERGENCY SERVICE</strong> - For information only
          </p>
          <p className="safety-banner-secondary">
            Always follow instructions from your state's fire and emergency services. In a life-threatening emergency, call <strong>000</strong>.
          </p>
        </div>
        <button
          className="safety-banner-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss safety banner"
          title="Dismiss permanently"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
